import React, { useEffect, useRef, useState } from 'react';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { puckArrayFieldPath } from '../../../utils/puckContentPath';
import { plainText } from '../../../utils/richTextHtml';
import { accentOrPrimary } from '../../../puck/fields/createPuckColorField';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import { parseMediaAspectRatioOption } from '../../../media/mediaFit';
import { normalizeMediaSource } from '../../../media/mediaSource';
import { borderCssFromProps, BORDERLESS_BORDER } from '../../../../shared/boxes/borderStyles';
import { boxShadowCss } from '../../../../shared/boxes/shadowStyles';
import { revealListItem } from '../../../puck/fields/listItemReveal';
import WithBlockSelection, { noSelection } from '../../../puck/WithBlockSelection';
import StoryMedia from './StoryMedia';
import classes from './ScrollStoryBlock.module.css';

function Story({
  selectBlock, id, steps = [], visualSide = 'right', visualWidth = 45,
  mediaAspectRatio = '16/9', gap = '2rem', transition = 'fade', accent = '',
  border = BORDERLESS_BORDER, shadow = 'none', textStyles = {}, puck,
}) {
  const isEditing = !!puck?.isEditing;
  const [picked, setPicked] = useState(0);
  // Hauteur du texte le plus long : réservée à toutes les étapes, pour que la liste garde la même hauteur d'une étape à l'autre.
  const rootRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useEffect(() => {
    const root = rootRef.current;
    const Observer = root?.ownerDocument.defaultView.ResizeObserver;
    const contents = root ? [...root.querySelectorAll('[data-story-content]')] : [];
    if (!Observer || !contents.length) return undefined;
    const measure = () => setBodyHeight(Math.ceil(Math.max(...contents.map((node) => node.getBoundingClientRect().height))));
    measure();
    const observer = new Observer(measure);
    contents.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [steps.length]);
  const active = Math.min(picked, Math.max(0, steps.length - 1));
  const ratio = parseMediaAspectRatioOption(mediaAspectRatio) || 16 / 9;
  const proportion = Math.max(25, Math.min(65, Number(visualWidth) || 45));

  const go = (index) => {
    const next = Math.max(0, Math.min(steps.length - 1, index));
    setPicked(next);
    if (isEditing) revealListItem(id, next);
  };
  const choose = (index) => (event) => {
    event.stopPropagation();
    selectBlock();
    go(index);
  };
  const editorProps = isEditing ? { 'data-puck-overlay-portal': 'true', onPointerDown: (event) => event.stopPropagation() } : {};

  return (
    <div ref={rootRef} id={id} className={classes.root} data-side={visualSide} data-editing={isEditing ? 'true' : undefined} data-transition={transition} style={{ '--story-visual-width': `${proportion}%`, '--story-gap': gap, '--story-body': `${bodyHeight}px`, '--story-accent': accentOrPrimary(accent) }}>
      {isEditing && !steps.length && <p>Ajoutez les étapes du déroulé dans l’inspecteur.</p>}
      <div className={classes.layout}>
        <ol className={classes.steps}>
          {steps.map((step, index) => {
            const isActive = index === active;
            return (
              <li key={index} className={classes.step} data-active={isActive ? 'true' : undefined} onFocus={isEditing ? () => go(index) : undefined}>
                <div className={classes.header}>
                  <button type="button" className={classes.number} style={textRoleCss(textStyles, 'number')} aria-expanded={isActive} aria-controls={`${id}-step-${index}`} aria-label={`Étape ${index + 1}${plainText(step.title) ? ` : ${plainText(step.title)}` : ''}`} onClick={choose(index)} {...editorProps}>{index + 1}</button>
                  <div className={classes.title} onClick={isEditing ? undefined : () => go(index)}>
                    <PuckInlineRichText key="title" id={id} puck={puck} html={step.title} contentPath={puckArrayFieldPath('steps', index, 'title')} style={{ display: 'block', fontSize: '1.2em', fontWeight: 600, ...textRoleCss(textStyles, 'title') }} reducedToolbar placeholder="Titre de l’étape" />
                  </div>
                </div>
                <div id={`${id}-step-${index}`} className={classes.body} inert={isActive ? undefined : true}>
                  <div className={classes.bodyInner}>
                    <div className={classes.bodyPad}><div data-story-content="true">
                    <PuckInlineRichText key="description" id={id} puck={puck} html={step.description} contentPath={puckArrayFieldPath('steps', index, 'description')} style={{ display: 'block', ...textRoleCss(textStyles, 'description') }} placeholder="Décrire cette étape" />
                    </div></div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        {steps.length > 1 && (
          <div className={classes.controls}>
            <button type="button" className={classes.arrow} aria-label="Étape précédente" disabled={active === 0} onClick={choose(active - 1)} {...editorProps}>‹</button>
            <span className={classes.counter} aria-live="polite">{active + 1} / {steps.length}</span>
            <button type="button" className={classes.arrow} aria-label="Étape suivante" disabled={active === steps.length - 1} onClick={choose(active + 1)} {...editorProps}>›</button>
          </div>
        )}
        {!!steps.length && (
          <figure className={classes.visual}>
            <div className={classes.stage} style={{ aspectRatio: ratio, ...borderCssFromProps(border), boxShadow: boxShadowCss(shadow) }}>
              {steps.map((step, index) => (
                <div key={index} className={classes.layer} data-active={index === active ? 'true' : undefined} aria-hidden={index === active ? undefined : 'true'}>
                  <StoryMedia key={`${index}:${normalizeMediaSource(step.media).src}`} media={step.media} alt={plainText(step.title)} isEditing={isEditing} ratio={ratio} active={index === active} blockId={id} path={puckArrayFieldPath('steps', index, 'media')} />
                </div>
              ))}
            </div>
          </figure>
        )}
      </div>
    </div>
  );
}

export default function ScrollStoryBlock(props) {
  const BlockSelection = props.puck?.isEditing ? WithBlockSelection : noSelection;
  return <BlockSelection blockId={props.id}>{(selectBlock) => <Story {...props} selectBlock={selectBlock} />}</BlockSelection>;
}
